<script setup lang="ts">
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { NativeSelectMode, NativeSelectOption } from '@shared/ui/fields'
import type { AppInputState } from '@shared/ui/inputs'
import type { TutorLegalAccountFieldConfig } from '../../model/legal/interfaces'

import { TutorBillingAccountType, useListsStore } from '@shared/model'
import { RowSlot } from '@shared/ui/other'
import { AppPhoneInput } from '@shared/ui/inputs'
import { NativeSelect } from '@shared/ui/fields'

import {
  TUTOR_LEGAL_EXCLUDED_PAYMENT_SERVICES,
  TUTOR_LEGAL_ACCOUNT_FIELDS,
  TUTOR_LEGAL_ACCOUNT_HOLDER_NAME_MAX_LENGTH,
} from '../../config/tutor-legal'
import { useTutorLegalStore } from '../../model/legal/store'
import { writeSanitizedHolderName } from '../../lib/billing'
import TutorLegalColumn from './TutorLegalColumn.vue'
import TutorLegalRows from './TutorLegalRows.vue'
import TutorLegalGroup from './TutorLegalGroup.vue'
import TutorLegalField from './TutorLegalField.vue'

defineOptions({
  name: 'TutorLegalBilling',
})

const tutorLegalStore = useTutorLegalStore()
const listsStore = useListsStore()

const paymentServiceOptions = computed<AppSelectOption[]>(() =>
  listsStore.getTutorBillingPaymentServiceOptions
    .filter(option => !TUTOR_LEGAL_EXCLUDED_PAYMENT_SERVICES.includes(String(option.value))))

const paymentServiceNativeOptions = computed<NativeSelectOption[]>(() =>
  paymentServiceOptions.value.map(option => ({
    id: option.value,
    value: option.value,
    text: option.text,
    selected: false,
  })))

const refPaymentServiceMode = computed<NativeSelectMode>(() =>
  tutorLegalStore.getFieldState('payment_service') === 'error' ? 'error' : 'normal')

const refAccountType = computed<TutorBillingAccountType | null>(() =>
  listsStore.getTutorBillingAccountType(tutorLegalStore.getForm.payment_service))

const refAccountField = computed<TutorLegalAccountFieldConfig>(() =>
  TUTOR_LEGAL_ACCOUNT_FIELDS[refAccountType.value ?? TutorBillingAccountType.Text]
    ?? TUTOR_LEGAL_ACCOUNT_FIELDS[TutorBillingAccountType.Text])

const refAccountInputState = computed<AppInputState | undefined>(() =>
  tutorLegalStore.getFieldState('account') === 'error' ? 'error' : undefined)

const refAccountMessage = computed<string | undefined>(() =>
  tutorLegalStore.getFieldState('account') === 'error' ? refAccountField.value.error : undefined)

const updatePaymentService = (option: NativeSelectOption): void => {
  const nextService = String(option.value ?? '')
  const typeChanged = listsStore.getTutorBillingAccountType(nextService) !== refAccountType.value

  tutorLegalStore.setFormField('payment_service', nextService)
  if (typeChanged) tutorLegalStore.setFormField('account', '')
}

const setAccountHolderName = (value: string): Promise<void> =>
  writeSanitizedHolderName(value, v => tutorLegalStore.setFormField('account_holder_name', v))
</script>

<template>
  <TutorLegalColumn class="tutor-legal-billing">
    <TutorLegalGroup title="Billing details">
      <NativeSelect
        name="payment_service"
        label="Payment service"
        placeholder="Select payment service"
        placeholder-enabled
        :value="tutorLegalStore.getForm.payment_service || null"
        :options="paymentServiceNativeOptions"
        :mode="refPaymentServiceMode"
        @change="updatePaymentService"
      />
    </TutorLegalGroup>

    <TutorLegalGroup>
      <TutorLegalRows>
        <RowSlot
          v-if="refAccountType === TutorBillingAccountType.Phone"
          :label="refAccountField.label"
        >
          <AppPhoneInput
            :model-value="tutorLegalStore.getForm.account"
            :state="refAccountInputState"
            :message="refAccountMessage"
            :invalid-msg="refAccountField.error"
            @update:model-value="value => tutorLegalStore.setFormField('account', value)"
          />
        </RowSlot>
        <TutorLegalField
          v-else
          :label="refAccountField.label"
          :placeholder="refAccountField.placeholder"
          :model-value="tutorLegalStore.getForm.account"
          :state="tutorLegalStore.getFieldState('account')"
          :message="tutorLegalStore.getFieldMessage('account')"
          @update:model-value="value => tutorLegalStore.setFormField('account', value)"
        />
        <TutorLegalField
          label="Account holder name"
          placeholder="Enter the account owner name"
          tooltip="The exact full name of the account holder as registered with the payment provider"
          :max-length="TUTOR_LEGAL_ACCOUNT_HOLDER_NAME_MAX_LENGTH"
          :model-value="tutorLegalStore.getForm.account_holder_name"
          :state="tutorLegalStore.getFieldState('account_holder_name')"
          @update:model-value="setAccountHolderName"
        />
        <TutorLegalField
          label="Billing ID"
          tooltip="Internal reference number (duplicates the tutor's admin ID)"
          :model-value="tutorLegalStore.getForm.billing_id"
          :state="tutorLegalStore.getFieldState('billing_id')"
          @update:model-value="value => tutorLegalStore.setFormField('billing_id', value)"
        />
      </TutorLegalRows>
    </TutorLegalGroup>
  </TutorLegalColumn>
</template>

<style lang="scss" scoped src="./TutorLegalBilling.scss" />
